import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BiEnvelope,
  BiLoaderAlt,
  BiLockAlt,
  BiCheckCircle,
} from "react-icons/bi";
import { GoBack } from "../../../components/Layout/GoBack";
import {
  getResetLinkRequest,
  clearGetResetLinkSuccess,
} from "../../../redux/action";

const ForgotPassword = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Using existing Redux state for forgot password
  const { getResetLinkSuccess, getResetLinkError, getResetLinkLoading } =
    useSelector((state) => {
      const {
        success: { getResetLink: getResetLinkSuccess },
        errors: { getResetLink: getResetLinkError },
      } = state.ajaxStatuses || { success: {}, errors: {} };

      const { getResetLinkLoading } = state.loadingIndicator || {};

      return {
        getResetLinkSuccess,
        getResetLinkError,
        getResetLinkLoading,
      };
    });

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .email("Please enter a valid email address")
        .required("Email is required"),
    }),

    onSubmit: (values) => {
      dispatch(getResetLinkRequest(values));
    },
  });

  useEffect(() => {
    if (getResetLinkError) {
      toast.error(getResetLinkError, {
        duration: 3000,
      });
    }
  }, [getResetLinkError]);

  useEffect(() => {
    if (getResetLinkSuccess) {
      setIsSubmitted(true);
      toast.success("Password reset link sent to your email!", {
        duration: 4000,
      });
      dispatch(clearGetResetLinkSuccess());
    }
  }, [getResetLinkSuccess, dispatch]);

  if (isSubmitted) {
    return (
      <div className="min-h-screen w-full relative flex items-center justify-center bg-slate-50 overflow-hidden font-body selection:bg-blue-100">
        {/* --- Ambient Background "Glam" (Light Mode) --- */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-200/40 rounded-full blur-[100px] pointer-events-none -translate-x-1/3 -translate-y-1/3 mix-blend-multiply"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-200/40 rounded-full blur-[100px] pointer-events-none translate-x-1/3 translate-y-1/3 mix-blend-multiply"></div>

        {/* --- Success Card --- */}
        <div className="relative z-10 w-full max-w-[420px] px-6">
          <GoBack />

          <div className="bg-white/60 backdrop-blur-xl border border-white/80 rounded-[2rem] p-8 md:p-10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>

            {/* Success Content */}
            <div className="text-center">
              <div className="mb-6">
                <BiCheckCircle
                  className="mx-auto text-green-500 mb-4"
                  size={64}
                />
                <h1 className="font-head text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-2">
                  Check your email
                </h1>
                <p className="text-slate-500 text-sm">
                  We've sent a password reset link to{" "}
                  <span className="font-semibold text-slate-700">
                    {formik.values.email}
                  </span>
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-xs text-slate-400">
                  Didn't receive the email? Check your spam folder or try again.
                </p>

                <button
                  onClick={() => setIsSubmitted(false)}
                  className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm rounded-xl transition-all"
                >
                  Try different email
                </button>

                <Link to="/signin">
                  <button className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 transform active:scale-[0.98] transition-all">
                    Back to Sign In
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Footer Security Seal */}
          <div className="mt-8 text-center">
            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest flex items-center justify-center gap-2">
              <BiLockAlt size={12} /> Secure 256-bit Encryption
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center bg-slate-50 overflow-hidden font-body selection:bg-blue-100">
      {/* --- Ambient Background "Glam" (Light Mode) --- */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-200/40 rounded-full blur-[100px] pointer-events-none -translate-x-1/3 -translate-y-1/3 mix-blend-multiply"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-200/40 rounded-full blur-[100px] pointer-events-none translate-x-1/3 translate-y-1/3 mix-blend-multiply"></div>

      {/* --- Main Glass Card --- */}
      <div className="relative z-10 w-full max-w-[420px] px-6">
        <GoBack />

        <div className="bg-white/60 backdrop-blur-xl border border-white/80 rounded-[2rem] p-8 md:p-10 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>

          {/* Header */}
          <div className="flex flex-col gap-y-2 mb-8 text-center">
            <h1 className="font-head text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
              Forgot password?
            </h1>
            <p className="text-slate-500 text-xs md:text-sm">
              No worries, we'll send you reset instructions.
            </p>
          </div>

          <form
            onSubmit={formik?.handleSubmit}
            className="flex flex-col gap-y-5"
          >
            {/* Email Field */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-3.5 text-slate-400 group-focus-within:text-blue-600 transition-colors">
                  <BiEnvelope size={20} />
                </div>
                <input
                  type="email"
                  {...formik?.getFieldProps("email")}
                  placeholder="Enter your email address"
                  className="w-full bg-white/70 border border-slate-200/80 text-slate-800 placeholder-slate-400 text-sm rounded-xl py-3.5 pl-11 pr-4 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all hover:bg-white hover:border-slate-300"
                  required
                />
              </div>
              {formik?.errors?.email && formik?.touched?.email && (
                <p className="text-red-500 text-xs ml-1 font-medium">
                  {formik.errors.email}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="pt-4 flex flex-col gap-y-6">
              <button
                type="submit"
                disabled={getResetLinkLoading}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 transform active:scale-[0.98] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2"
              >
                {getResetLinkLoading ? (
                  <>
                    <BiLoaderAlt className="animate-spin" size={20} />
                    <span>Sending...</span>
                  </>
                ) : (
                  "Reset password"
                )}
              </button>

              <div className="text-center">
                <Link to="/signin">
                  <span className="text-sm text-slate-500">
                    <span className="text-blue-600 font-bold hover:text-blue-700 transition-colors cursor-pointer">
                      ← Back to sign in
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </form>
        </div>

        {/* Footer Security Seal */}
        <div className="mt-8 text-center">
          <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest flex items-center justify-center gap-2">
            <BiLockAlt size={12} /> Secure 256-bit Encryption
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
